/**
 * useCurrentUser Hook
 *
 * Provides the current logged-in user's ID and display name for use
 * throughout the application (activities, notes, tasks, etc.).
 *
 * PE-1454: Replaces hardcoded "Current User" with actual user name.
 */

import { useAuth } from '../contexts/AuthContext';

interface CurrentUser {
  userId: string;
  userName: string;
  email: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Hook to get the current user's info for activity logging
 *
 * @returns CurrentUser object with userId, userName (display name), and email
 *
 * @example
 * const { userId, userName } = useCurrentUser();
 * activityStorage.create({
 *   userId,
 *   userName,
 *   // ...
 * });
 */
export function useCurrentUser(): CurrentUser {
  const { user } = useAuth();

  // Build display name from firstName + lastName, fallback to email or 'Unknown User'
  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return {
    userId: user?.id || 'unknown',
    userName: fullName || user?.email || 'Unknown User',
    email: user?.email || '',
    firstName: user?.firstName,
    lastName: user?.lastName,
  };
}
